/**
 * ----------------------------------------------------------------------------------------------------------------
 * ANNOUNCEMENT COUNTDOWN
 * ----------------------------------------------------------------------------------------------------------------
 * Lightweight, dependency-free countdown timer custom element used by sections/announcement-bar.liquid (via
 * snippets/announcement-countdown.liquid). Not part of the shared theme.js bundle on purpose - this is genuinely
 * new functionality (no countdown timer exists anywhere else in the Impact theme), so it ships as its own small
 * asset instead of touching the large shared bundle.
 *
 * Contract (attributes set by the Liquid snippet):
 *   end-date        "YYYY-MM-DD"
 *   end-time        "HH:MM" (24h)
 *   timezone        IANA timezone, e.g. "Europe/Amsterdam"
 *   format          "compact" (03:21:48) or "long" (2d 03u 21m 48s)
 *   hide-on-expire   boolean attribute - if present AND no [data-expired] message exists, hides the closest
 *                    .announcement-message via the `hidden` attribute once the countdown reaches zero. Setting the
 *                    `hidden` attribute (rather than inline display:none) deliberately mirrors how the theme's own
 *                    BaseCarousel._filterItems already hides items (item.hidden = ...), since BaseCarousel's
 *                    `visibleItems` getter filters on `offsetParent !== null` - so a hidden announcement is
 *                    correctly skipped by the slider/multiple layouts without any extra carousel-aware code here.
 *
 * Accessibility: the live digits are aria-hidden (re-announcing every second would be disruptive); a single static
 * sr-only label describes the countdown once. No layout shift: digits use tabular-nums (see the section's CSS) and
 * only textContent is written on tick, never new DOM nodes.
 */
(function () {
  "use strict";

  var AnnouncementCountdown = /** @class */ (function () {
    class AnnouncementCountdown extends HTMLElement {
      connectedCallback() {
        this._activeEl = this.querySelector("[data-active]");
        this._expiredEl = this.querySelector("[data-expired]");
        this._timerEl = this.querySelector("[data-timer]");
        this._format = this.getAttribute("format") === "long" ? "long" : "compact";
        this._hideOnExpire = this.hasAttribute("hide-on-expire");
        this._intervalId = null;

        const endDate = this.getAttribute("end-date");
        const endTime = this.getAttribute("end-time") || "00:00";
        const timezone = this.getAttribute("timezone") || "Europe/Amsterdam";

        if (!endDate || !this._timerEl) {
          return;
        }

        this._targetTimestamp = AnnouncementCountdown.zonedTimeToUtcTimestamp(endDate, endTime, timezone);

        if (Number.isNaN(this._targetTimestamp)) {
          return;
        }

        this._boundTick = this._tick.bind(this);
        this._boundVisibilityChange = this._onVisibilityChange.bind(this);
        document.addEventListener("visibilitychange", this._boundVisibilityChange);

        this._tick();
      }

      disconnectedCallback() {
        this._stopInterval();
        if (this._boundVisibilityChange) {
          document.removeEventListener("visibilitychange", this._boundVisibilityChange);
        }
      }

      _onVisibilityChange() {
        if (document.hidden) {
          this._stopInterval();
        } else {
          this._tick();
        }
      }

      _startInterval() {
        if (this._intervalId || document.hidden) {
          return;
        }
        this._intervalId = window.setInterval(this._boundTick, 1e3);
      }

      _stopInterval() {
        if (this._intervalId) {
          window.clearInterval(this._intervalId);
          this._intervalId = null;
        }
      }

      _tick() {
        const remainingMs = this._targetTimestamp - Date.now();

        if (remainingMs <= 0) {
          this._renderRemaining(0);
          this._stopInterval();
          this._onExpire();
          return;
        }

        this._renderRemaining(remainingMs);
        this._startInterval();
      }

      _renderRemaining(remainingMs) {
        const totalSeconds = Math.max(0, Math.floor(remainingMs / 1e3));
        const days = Math.floor(totalSeconds / 86400);
        const hours = Math.floor((totalSeconds % 86400) / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const seconds = totalSeconds % 60;
        const pad = (value) => String(value).padStart(2, "0");

        if (this._format === "long") {
          this._timerEl.textContent = `${days}d ${pad(hours)}u ${pad(minutes)}m ${pad(seconds)}s`;
        } else {
          const hoursDisplay = days > 0 ? days * 24 + hours : hours;
          this._timerEl.textContent = `${pad(hoursDisplay)}:${pad(minutes)}:${pad(seconds)}`;
        }
      }

      _onExpire() {
        if (this._expiredEl) {
          if (this._activeEl) {
            this._activeEl.hidden = true;
          }
          this._expiredEl.hidden = false;
        } else if (this._hideOnExpire) {
          const message = this.closest(".announcement-message");
          if (message) {
            message.hidden = true;
          }
        }

        this.dispatchEvent(new CustomEvent("announcement-countdown:expired", { bubbles: true }));
      }

      /**
       * Converts a wall-clock date + time in an arbitrary IANA timezone into a UTC timestamp (ms), DST-safe,
       * without an external date library. Standard two-pass Intl.DateTimeFormat trick: interpret the wall clock
       * as if it were UTC, format that instant back into the target timezone, and use the difference as the
       * timezone's offset at that instant (accurate across DST since we resolve the offset for the actual date,
       * not a fixed constant).
       */
      static zonedTimeToUtcTimestamp(dateStr, timeStr, timeZone) {
        const dateParts = dateStr.split("-").map(Number);
        const timeParts = timeStr.split(":").map(Number);
        const year = dateParts[0], month = dateParts[1], day = dateParts[2];
        const hour = timeParts[0], minute = timeParts[1];

        if (!year || !month || !day || Number.isNaN(hour) || Number.isNaN(minute)) {
          return NaN;
        }

        const asUTC = Date.UTC(year, month - 1, day, hour, minute, 0);

        let formatter;
        try {
          formatter = new Intl.DateTimeFormat("en-US", {
            timeZone,
            hourCycle: "h23",
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit"
          });
        } catch (error) {
          return asUTC;
        }

        const partsToUTC = (date) => {
          const parts = formatter.formatToParts(date).reduce((acc, part) => {
            acc[part.type] = part.value;
            return acc;
          }, {});
          const partHour = Number(parts.hour) === 24 ? 0 : Number(parts.hour);
          return Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), partHour, Number(parts.minute), Number(parts.second));
        };

        const offset = partsToUTC(new Date(asUTC)) - asUTC;
        return asUTC - offset;
      }
    }

    return AnnouncementCountdown;
  })();

  if (!window.customElements.get("announcement-countdown")) {
    window.customElements.define("announcement-countdown", AnnouncementCountdown);
  }
})();